// src/pages/BlindStructures.js
import React, { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import { auth, db } from "../firebase";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";

const COPPER = "#B87333";

function segmentLabel(item) {
  if (!item) return "—";
  if (item.type === "break") return `Break — ${item.label || "Break"} (${item.minutes || 0}m)`;
  const anteText = item.ante ? ` (${item.ante})` : "";
  return `Level ${item.level}: ${item.sb}/${item.bb}${anteText} • ${item.minutes || 0}m`;
}

export default function BlindStructures() {
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);

  // Editor
  const [mode, setMode] = useState("create"); // create | edit
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState("");
  const [blinds, setBlinds] = useState([]);
  const [saving, setSaving] = useState(false);

  const levelsCount = useMemo(
    () => blinds.filter((b) => b.type === "level").length,
    [blinds]
  );

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const ref = collection(db, "blindStructures");
      const q = query(ref, orderBy("updatedAt", "desc"));
      const snap = await getDocs(q);
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setTemplates(data);
    } catch (e) {
      console.error("Load blindStructures failed:", e);
      alert("Could not load blind structures.");
    }
    setLoading(false);
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  // ===== Editor helpers (same feel as TournamentDetails) =====
  const addLevel = () => {
    const nextLevel = blinds.filter((b) => b.type === "level").length + 1;
    setBlinds((prev) => [
      ...prev,
      { type: "level", level: nextLevel, sb: 100, bb: 200, ante: 0, minutes: 20 },
    ]);
  };

  const addBreak = () => setBlinds((prev) => [...prev, { type: "break", label: "Break", minutes: 10 }]);

  const removeItem = (idx) => setBlinds((prev) => prev.filter((_, i) => i !== idx));

  const updateItem = (idx, patch) =>
    setBlinds((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  const renumberLevels = () => {
    let lvl = 1;
    setBlinds((prev) =>
      prev.map((item) => (item.type === "level" ? { ...item, level: lvl++ } : item))
    );
  };

  const resetEditor = () => {
    setMode("create");
    setEditingId(null);
    setName("");
    setBlinds([]);
  };

  const startEdit = (tpl) => {
    setMode("edit");
    setEditingId(tpl.id);
    setName(tpl.name || "");
    setBlinds(Array.isArray(tpl.blinds) ? tpl.blinds : []);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const saveTemplate = async () => {
    const trimmed = String(name || "").trim();
    if (!trimmed) return alert("Please enter a name for this structure.");
    if (!Array.isArray(blinds) || blinds.length === 0) return alert("Add at least one level/break.");

    setSaving(true);
    try {
      const payload = {
        name: trimmed,
        blinds,
        updatedAt: serverTimestamp(),
      };

      if (mode === "create") {
        await addDoc(collection(db, "blindStructures"), {
          ...payload,
          createdAt: serverTimestamp(),
          createdByUid: auth.currentUser?.uid || null,
        });
      } else if (mode === "edit" && editingId) {
        await updateDoc(doc(db, "blindStructures", editingId), payload);
      }

      await loadTemplates();
      resetEditor();
      alert("Blind structure saved.");
    } catch (e) {
      console.error("Save blind structure failed:", e);
      alert("Could not save blind structure.");
    }
    setSaving(false);
  };

  const deleteTemplate = async (tpl) => {
    if (!tpl?.id) return;
    const ok = window.confirm(`Delete "${tpl.name}"? This cannot be undone.`);
    if (!ok) return;

    try {
      await deleteDoc(doc(db, "blindStructures", tpl.id));
      await loadTemplates();
      if (editingId === tpl.id) resetEditor();
    } catch (e) {
      console.error("Delete blind structure failed:", e);
      alert("Could not delete blind structure.");
    }
  };

  return (
    <Layout>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="text-3xl font-bold" style={{ color: COPPER }}>
            Blind Structures
          </h2>
          <p className="text-zinc-400 mt-1">
            Save your common blind templates and reuse them for new tournaments.
          </p>
        </div>

        <button
          onClick={resetEditor}
          className="px-4 py-2 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-100 font-semibold"
        >
          New Template
        </button>
      </div>

      {/* Editor */}
      <div className="bg-zinc-900 p-4 rounded-lg border border-zinc-800 mb-8">
        <div className="flex items-center justify-between gap-4 mb-4">
          <div>
            <h3 className="text-xl font-semibold" style={{ color: COPPER }}>
              {mode === "edit" ? "Edit Template" : "Create Template"}
            </h3>
            <p className="text-zinc-500 text-sm">
              {levelsCount} level(s) • {blinds.length} total item(s)
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={addLevel}
              className="px-4 py-2 rounded text-zinc-950 font-semibold"
              style={{ backgroundColor: COPPER }}
            >
              + Add Level
            </button>

            <button
              onClick={addBreak}
              className="px-4 py-2 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-100 font-semibold"
            >
              + Add Break
            </button>

            <button
              onClick={renumberLevels}
              className="px-4 py-2 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-100 font-semibold"
            >
              Renumber
            </button>

            <button
              onClick={saveTemplate}
              disabled={saving}
              className={`px-4 py-2 rounded font-semibold ${
                saving ? "bg-zinc-700 text-zinc-300 cursor-not-allowed" : "bg-green-700 hover:bg-green-800 text-white"
              }`}
            >
              {saving ? "Saving..." : "Save Template"}
            </button>
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-2 items-end mb-4">
          <div>
            <label className="block text-zinc-300 text-sm mb-1">Template Name</label>
            <input
              type="text"
              className="w-full p-3 bg-zinc-800 text-zinc-100 rounded"
              placeholder='e.g. "RRPC Standard Turbo"'
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="text-zinc-500 text-sm">
            Tip: Keep a “Standard” and a “Turbo” template so you’re never rebuilding this.
          </div>
        </div>

        {blinds.length === 0 ? (
          <p className="text-zinc-400">No items yet. Add a level or break above.</p>
        ) : (
          <div className="space-y-3">
            {blinds.map((item, idx) => (
              <div key={idx} className="p-3 rounded border border-zinc-800 bg-zinc-950">
                {item.type === "level" ? (
                  <div className="grid gap-3 md:grid-cols-6 items-end">
                    <div className="md:col-span-1">
                      <p className="text-zinc-400 text-sm">Level</p>
                      <p className="text-zinc-100 font-semibold">{item.level}</p>
                    </div>

                    <div>
                      <label className="block text-zinc-400 text-sm">SB</label>
                      <input
                        type="number"
                        className="w-full p-2 bg-zinc-800 text-zinc-100 rounded"
                        value={item.sb}
                        onChange={(e) => updateItem(idx, { sb: Number(e.target.value) })}
                      />
                    </div>

                    <div>
                      <label className="block text-zinc-400 text-sm">BB</label>
                      <input
                        type="number"
                        className="w-full p-2 bg-zinc-800 text-zinc-100 rounded"
                        value={item.bb}
                        onChange={(e) => updateItem(idx, { bb: Number(e.target.value) })}
                      />
                    </div>

                    <div>
                      <label className="block text-zinc-400 text-sm">Ante</label>
                      <input
                        type="number"
                        className="w-full p-2 bg-zinc-800 text-zinc-100 rounded"
                        value={item.ante || 0}
                        onChange={(e) => updateItem(idx, { ante: Number(e.target.value) })}
                      />
                    </div>

                    <div>
                      <label className="block text-zinc-400 text-sm">Minutes</label>
                      <input
                        type="number"
                        className="w-full p-2 bg-zinc-800 text-zinc-100 rounded"
                        value={item.minutes}
                        onChange={(e) => updateItem(idx, { minutes: Number(e.target.value) })}
                      />
                    </div>

                    <div className="md:col-span-1 flex justify-end">
                      <button
                        onClick={() => removeItem(idx)}
                        className="px-3 py-2 rounded bg-red-600 hover:bg-red-700 text-white font-semibold"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="grid gap-3 md:grid-cols-6 items-end">
                    <div className="md:col-span-2">
                      <label className="block text-zinc-400 text-sm">Break Label</label>
                      <input
                        type="text"
                        className="w-full p-2 bg-zinc-800 text-zinc-100 rounded"
                        value={item.label || "Break"}
                        onChange={(e) => updateItem(idx, { label: e.target.value })}
                      />
                    </div>

                    <div>
                      <label className="block text-zinc-400 text-sm">Minutes</label>
                      <input
                        type="number"
                        className="w-full p-2 bg-zinc-800 text-zinc-100 rounded"
                        value={item.minutes}
                        onChange={(e) => updateItem(idx, { minutes: Number(e.target.value) })}
                      />
                    </div>

                    <div className="md:col-span-2">
                      <p className="text-zinc-100 font-semibold">Preview</p>
                      <p className="text-zinc-400 text-sm">{segmentLabel(item)}</p>
                    </div>

                    <div className="md:col-span-1 flex justify-end">
                      <button
                        onClick={() => removeItem(idx)}
                        className="px-3 py-2 rounded bg-red-600 hover:bg-red-700 text-white font-semibold"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* List */}
      <div className="bg-zinc-900 p-4 rounded-lg border border-zinc-800">
        <h3 className="text-xl font-semibold mb-4" style={{ color: COPPER }}>
          Saved Templates
        </h3>

        {loading ? (
          <p className="text-zinc-400">Loading templates...</p>
        ) : templates.length === 0 ? (
          <p className="text-zinc-400">No templates saved yet.</p>
        ) : (
          <div className="space-y-3">
            {templates.map((tpl) => (
              <div
                key={tpl.id}
                className="p-4 rounded border border-zinc-800 bg-zinc-950 flex items-start justify-between gap-4"
              >
                <div>
                  <p className="text-zinc-100 font-semibold text-lg">{tpl.name}</p>
                  <p className="text-zinc-500 text-sm mt-1">
                    Items: {Array.isArray(tpl.blinds) ? tpl.blinds.length : 0}
                  </p>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => startEdit(tpl)}
                    className="px-4 py-2 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-100 font-semibold"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => deleteTemplate(tpl)}
                    className="px-4 py-2 rounded bg-red-600 hover:bg-red-700 text-white font-semibold"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}